import { FC, useEffect, useRef } from 'react'
import { useAuth } from '@frontegg/react'
import { useQuery } from 'react-query'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { useSelector } from 'react-redux'

/* Utils */
import axios from 'lib/axios'
import { CPILibraryItem, CPITimePeriod, DataStatus } from 'models'
import useLibraryDetailsData, { DevicesTabs } from './useLibraryDetailsData'
import useDevicesData from './useDevicesData'
import { getPayloadTimePeriod } from '../library/utils'
import { useAppDispatch } from 'store/hooks'
import { useLibraryDetailsState, usePredictionState } from './utils/hooks'
import { closeToast, openToast, ToastModel, ToastVariant } from 'store/toasts/toastSlice'
import { LibraryDetailsProvider } from '../../../contexts/LibraryDetailsContext'
import { openInNewTab } from './utils/libraryDetailsUtils'

/* Components */
import { LibraryDetailsContainer, LibraryDetailsScrollArea } from './cpiDetails.styles'
import { LoadingWrapper } from '../library/library.styles'
import LibraryDetailsHeader from './components/LibraryDetailsHeader'
import CPILibrarySettingsDrawer from './sections/settings-drawer'
import { accountSettingsSelector } from 'store/user/userSlice'
import TabPanels from './sections/tab-panels/TabPanels'

dayjs.extend(relativeTime)

export interface Checked {
  [key: string]: boolean
}

export interface CustomColors {
  chip_bg_color: string
  chip_color: string
}

const CPIDetails: FC = () => {
  const { cpiName } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useAppDispatch()
  const closeNotification = useRef<any>(null)
  const accountSettings = useSelector(accountSettingsSelector)
  const { user: authUser } = useAuth()

  const detailsState = useLibraryDetailsState({ cpiName })
  const predictionState = usePredictionState()

  const {
    drawerOpen,
    setDrawerOpen,
    chartData,
    setChartData,
    code,
    setCode,
    defaultValues,
    setDefaultValues,
    criticalSla,
    setCriticalSla,
    fetchingInterval,
    setFetchingInterval,
    emptyInfoTooltipOpen,
    setEmptyInfoTooltipOpen,
    slaDescriptionTooltipOpen,
    setSlaDescriptionTooltipOpen,
    healthDirection,
    setHealthDirection,
    isExportingReport,
    setIsExportingReport,
    showMessage,
    setShowMessage,
    recalculatingCpiName,
    setRecalculatingCpiName,
    currentSeverityStatus,
    setCurrentSeverityStatus,
    renderingChart,
    setRenderingChart,
    mode,
    setMode,
    redirectToMode,
    setRedirectToMode,
    handleEmptyInfoClick,
    handleSlaDescriptionTooltipClick,
    isMediumHeight,
    labelDrawerOpen,
    setLabelDrawerOpen,
    thisCpiName,
    defaultRecentValueDate,
    setDefaultRecentValueDate,
    recentInitialLoad,
    setRecentInitialLoad,
    editLabel,
    setEditLabel
  } = detailsState

  const { predictionModalOpen, handlePredictionModalOpen, handlePredictionModalClose } = predictionState

  const {
    key,
    setKey,
    userConfiguredIntegrations,
    cpi,
    cpiConfiguration,
    manifestData,
    competitorAnalysisData,
    detailedComparisonData,
    active,
    showBenchmarkingTab,
    activeSources,
    checked,
    setChecked,
    notConfiguredInstancesApplicableToCpi,
    cpiSupportedAndUserConfiguredList,
    dataSourceValue,
    dataSourceOptions,
    deactivateCpi,
    favorite,
    updateFavorite,
    selectedSeverity,
    selectedTimePeriod,
    timePeriodValues,
    frequencyValues,
    selectedFrequency,
    setSelectedSeverity,
    setSelectedTimePeriod,
    setSelectedFrequency,
    setDataSourceValue,
    selectedDataSourceIds,
    setSelectedDataSourceIds,
    fetchCpiConfig,
    fetchAll,
    stripes,
    setStripes,
    customTimePeriodValue,
    setCustomTimePeriodValue,
    hasPredictionsEnabled,
    refetchPredictionData,
    predictionModelAvailable,
    daysUntilDataAvailable,
    selectedType,
    setSelectedType,
    customColors,
    customFramework,
    fw,
    fetchPredictionData,
    tabValue,
    setTabValue,
    predictionFetched,
    isSnowflake,
    allCpis,
    initialLoad,
    setInitialLoad,
    predictionData,
    setPredictionData,
    deactivateCpiV1,
    setDeactivateCpiV1,
    potentialSources,
    triggerTrendsRefetch,
    trendsRefetchTrigger,
    manifestV2
  } = useLibraryDetailsData({ cpiName })

  const {
    fetchQuery,
    initialQueryLoaded,
    devicesPagination,
    getLookup,
    columnValues,
    setColumnValues,
    columnsFetched,
    setColumnsFetched,
    snowflakeManifest,
    setInitialQueryLoaded,
    metricManifest
  } = useDevicesData({
    thisCpiName,
    cpiConfiguration,
    dataSourceOptions
  })

  // Track chartData at the time of config change to detect when new data arrives
  const pendingRefetchChartDataRef = useRef<typeof chartData>(null)

  useEffect(() => {
    setRecentInitialLoad(false)
    setDefaultRecentValueDate({
      date: '',
      value: '',
      denominator: '0',
      lastValue: ''
    })
    pendingRefetchChartDataRef.current = null
  }, [cpiName])

  const contextValue = {
    manifestV2,
    potentialSources,
    fetchingInterval,
    setFetchingInterval,
    hasPredictionsEnabled,
    predictionModelAvailable,
    refetchPredictionData,
    daysUntilDataAvailable,
    setDataSourceValue,
    setCurrentSeverityStatus,
    metricManifest,
    timePeriodValues,
    fetchPredictionData,
    healthDirection,
    activeSources,
    setInitialQueryLoaded,
    authUser,
    cpi,
    manifestData,
    chartData,
    setChartData,
    renderingChart,
    setRenderingChart,
    currentSeverityStatus,
    selectedSeverity,
    setSelectedSeverity,
    selectedTimePeriod,
    setSelectedTimePeriod,
    selectedFrequency,
    setSelectedFrequency,
    selectedDataSourceIds,
    setSelectedDataSourceIds,
    drawerOpen,
    setDrawerOpen,
    labelDrawerOpen,
    setLabelDrawerOpen,
    handleEmptyInfoClick,
    handleSlaDescriptionTooltipClick,
    handlePredictionModalOpen,
    handlePredictionModalClose,
    predictionFetched,
    isSnowflake,
    mode,
    setMode,
    stripes,
    setStripes,
    initialLoad,
    setInitialLoad,
    active,
    fw,
    isMediumHeight,
    cpiConfiguration,
    selectedType,
    setSelectedType,
    criticalSla,
    setCriticalSla,
    showMessage,
    setShowMessage,
    recalculatingCpiName,
    setRecalculatingCpiName,
    defaultValues,
    cpiSupportedAndUserConfiguredList,
    emptyInfoTooltipOpen,
    setEmptyInfoTooltipOpen,
    userConfiguredIntegrations,
    tabValue,
    setTabValue,
    showBenchmarkingTab,
    predictionData,
    setPredictionData,
    customTimePeriodValue,
    frequencyValues,
    dataSourceOptions,
    dataSourceValue,
    setCustomTimePeriodValue,
    redirectToMode,
    setRedirectToMode,
    setHealthDirection,
    predictionModalOpen,
    notConfiguredInstancesApplicableToCpi,
    deactivateCpiV1,
    setDeactivateCpiV1,
    recentInitialLoad,
    defaultRecentValueDate,
    setDefaultRecentValueDate,
    triggerTrendsRefetch,
    trendsRefetchTrigger,
    snowflakeManifest,
    editLabel,
    setEditLabel
  }

  const { data, error, isFetching } = useQuery(
    ['code', { code }],
    () => axios.get(`/api/v1/reports/download_url/${code}`),
    {
      refetchInterval: 5000,
      enabled: !!code
    }
  )

  useEffect(() => {
    if (location.state && (location.state as any).isAboutTab) {
      if (active) {
        setTabValue(snowflakeManifest ? DevicesTabs.Benchmarking : DevicesTabs.About)
      } else {
        setTabValue(DevicesTabs.About)
      }
      setTimeout(() => {
        navigate(location.pathname, {})
      }, 2000)
      setKey(key + 1)
    } else if (location.state && (location.state as any).openConfig) {
      setDrawerOpen(true)
      setTimeout(() => {
        navigate(location.pathname, {})
      }, 2000)
      setKey(key + 1)
    } else if (location.hash === '#about' && active) {
      return setTabValue(DevicesTabs.About)
    }
  }, [location, active])

  useEffect(() => {
    if (data && !error && !isFetching) {
      const downloadUrl = data.data.reportDownloadUrl.url
      openInNewTab(`${downloadUrl}`)
      dispatch(
        openToast({
          variant: ToastVariant.success,
          toastModel: ToastModel.Trends,
          props: {
            text: `${cpi?.display_name} Report PDF Successfully Downloaded`,
            description: ''
          }
        })
      )
      closeNotification.current = setTimeout(() => {
        dispatch(closeToast())
      }, 5000)
      setCode('')
    }
  }, [data, error, isFetching, code])

  useEffect(() => {
    // Get supported property names from metricManifest (only for Snowflake CPIs)
    const getSupportedNames = () => {
      if (!isSnowflake || !metricManifest?.spec?.ui?.trend_chart?.user_input) return null

      const supportedUserInput = metricManifest.spec.ui.trend_chart.user_input
      const supportedNames = new Set<string>()

      // Add visualization_param section names
      if (supportedUserInput.visualization_param?.sections) {
        supportedUserInput.visualization_param.sections.forEach((section: { name: string }) => {
          supportedNames.add(section.name)
        })
      }

      // Add formula_params section names
      if (supportedUserInput.formula_params?.length > 0) {
        supportedUserInput.formula_params.forEach((param: { name: string; sections?: { name: string }[] }) => {
          if (param && param.sections && param.sections?.length > 0) {
            param.sections.forEach((section) => supportedNames.add(section.name))
          } else if (param.name) {
            supportedNames.add(param.name)
          }
        })
      }

      return supportedNames.size > 0 ? supportedNames : null
    }

    // Only apply filtering for Snowflake CPIs
    const supportedNames = isSnowflake ? getSupportedNames() : null

    if (cpiConfiguration && active) {
      const values: any = {
        cpiCategoryValue: ''
      }
      if (cpiConfiguration.user_input.visualization_param.identical_section_values) {
        values.sla = cpiConfiguration.user_input.visualization_param.identical_value
      }
      if (
        !cpiConfiguration.user_input.visualization_param.identical_section_values &&
        cpiConfiguration.user_input.visualization_param.sections
      ) {
        cpiConfiguration.user_input.visualization_param.sections.forEach(({ name, value }) => {
          // Only filter if isSnowflake and supportedNames is available
          if (!supportedNames || supportedNames.has(name)) {
            values[name] = value
          }
        })
      }
      cpiConfiguration.user_input.formula_params?.forEach((param) => {
        if (param.sections.length > 0) {
          param.sections.forEach(({ name, value }) => {
            // Only filter if isSnowflake and supportedNames is available
            if (!supportedNames || supportedNames.has(name)) {
              values[name] = value
            }
          })
        } else if (param.value) {
          // Only filter if isSnowflake and supportedNames is available
          if (!supportedNames || supportedNames.has(param.name)) {
            values[param.name] = param.value
          }
        }
      })
      setDefaultValues(values)
    } else if (manifestData) {
      const values: any = {
        cpiCategoryValue: ''
      }
      if (manifestData.ui.trend_chart.user_input.visualization_param.identical_section_values) {
        values.sla = manifestData.ui.trend_chart.user_input.visualization_param.identical_value
      }
      if (!manifestData.ui.trend_chart.user_input.visualization_param.identical_section_values) {
        manifestData.ui.trend_chart.user_input.visualization_param.sections.forEach(({ name, value }) => {
          // Only filter if isSnowflake and supportedNames is available
          if (!supportedNames || supportedNames.has(name)) {
            values[name] = value
          }
        })
      }
      manifestData.ui.trend_chart.user_input.formula_params?.forEach((param) => {
        if (param.sections.length > 0) {
          param.sections.forEach(({ name, value }) => {
            // Only filter if isSnowflake and supportedNames is available
            if (!supportedNames || supportedNames.has(name)) {
              values[name] = value
            }
          })
        } else if (param.value) {
          // Only filter if isSnowflake and supportedNames is available
          if (!supportedNames || supportedNames.has(param.name)) {
            values[param.name] = param.value
          }
        }
      })
      setDefaultValues(values)
    }
    // `active` selects between the saved config and the manifest defaults below, so deactivating
    // has to recompute — otherwise the settings panel reopens on the deactivated CPI's old SLAs.
  }, [manifestData, cpiConfiguration, metricManifest, isSnowflake, active])

  useEffect(() => {
    if (tabValue === DevicesTabs.Trends && location.hash === '#devices') {
      navigate(location.pathname, {})
      setTabValue(DevicesTabs.Trends)
    }
  }, [tabValue, location])

  const handleChangeInstance = (isChecked: boolean, name: string) => {
    setChecked((checked) => ({
      ...checked,
      [name]: isChecked
    }))
  }

  const handleReportExport = async () => {
    if (!cpi) return

    if (!manifestData) return

    const thisSection = manifestData.ui.trend_chart.user_input.visualization_param.sections.find(
      (n) => n.display_name === selectedSeverity
    )

    let timePeriod: '1w' | '1m' | '3m' | '6m' | '1y' | undefined = getPayloadTimePeriod(selectedTimePeriod)
    let dateFrom = ''
    let dateTo = ''
    if (selectedTimePeriod === CPITimePeriod.Custom && customTimePeriodValue.length > 0) {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const firstPart = customTimePeriodValue.slice(0, 3)
      const firstMonth = months.indexOf(firstPart) + 1
      const firstMonthFormatted = `${firstMonth}`.padStart(2, '0')
      const endYearPart = customTimePeriodValue.slice(-2)
      const endYear = `20${endYearPart}`

      if (customTimePeriodValue.length > 13) {
        const secondPart = customTimePeriodValue.slice(8, 11)
        const secondMonth = months.indexOf(secondPart) + 1
        const secondMonthFormatted = `${secondMonth}`.padStart(2, '0')
        const startYearPart = customTimePeriodValue.slice(5, 7)
        const startYear = `20${startYearPart}`
        const daysInMonth = dayjs(`${endYear}-${secondMonthFormatted}-01`).daysInMonth()

        dateFrom = `${startYear}-${firstMonthFormatted}-01`
        dateTo = `${endYear}-${secondMonthFormatted}-${daysInMonth}`
        timePeriod = undefined
      } else {
        const secondPart = customTimePeriodValue.slice(4, 7)
        const secondMonth = months.indexOf(secondPart) + 1
        const secondMonthFormatted = `${secondMonth}`.padStart(2, '0')
        const daysInMonth = dayjs(`${endYear}-${secondMonthFormatted}-01`).daysInMonth()

        dateFrom = `${endYear}-${firstMonthFormatted}-01`
        dateTo = `${endYear}-${secondMonthFormatted}-${daysInMonth}`
        timePeriod = undefined
      }
    }
    const payload = {
      cpi_name: cpi?.name,
      frequency: selectedFrequency,
      integration_ids: selectedDataSourceIds,
      section: thisSection?.name,
      timePeriod,
      dateFrom: dateFrom,
      dateTo: dateTo
    }

    setIsExportingReport(true)

    try {
      const res = await axios.post(`/api/v1/reports/cpi`, payload)

      if (res.status.toString().startsWith('2')) {
        const { code } = res.data
        setCode(code)
      }
    } catch (e) {
      dispatch(
        openToast({
          variant: ToastVariant.error,
          props: {
            text: 'Failed to Export Report',
            description: 'Please Contact Our Support Team'
          }
        })
      )
    } finally {
      setIsExportingReport(false)
    }
  }

  if (!initialLoad) {
    return (
      <LoadingWrapper>
        <CircularProgress size="40px" />
      </LoadingWrapper>
    )
  }
  return (
    <Box key={key}>
      {!manifestData ? (
        <Typography textAlign="center" mt={4}>
          CPI not found
        </Typography>
      ) : (
        <LibraryDetailsProvider value={contextValue}>
          <LibraryDetailsContainer>
            <LibraryDetailsHeader
              activeCpis={allCpis.filter((item: CPILibraryItem) => item.configuration)}
              columnsFetched={columnsFetched}
              setColumnsFetched={setColumnsFetched}
              customColors={customColors}
              favorite={favorite}
              updateFavorite={updateFavorite}
              handleReportExport={handleReportExport}
              isExportingReport={isExportingReport || isFetching}
              disableReport={!(chartData && chartData.status === DataStatus.Ready)}
              accountSettings={accountSettings}
              handlePredictionModalClose={handlePredictionModalClose}
              initialQueryLoaded={initialQueryLoaded}
            />

            <LibraryDetailsScrollArea>
              <TabPanels
                columnValues={columnValues}
                setColumnValues={setColumnValues}
                getLookup={getLookup}
                devicesPagination={devicesPagination}
                thisCpiName={thisCpiName}
                fetchQuery={fetchQuery}
                competitorAnalysisData={competitorAnalysisData}
                detailedComparisonData={detailedComparisonData}
                customerName={authUser?.name || ''}
                setColumnsFetched={setColumnsFetched}
              />
            </LibraryDetailsScrollArea>

            <CPILibrarySettingsDrawer
              customFramework={customFramework}
              fetchAll={fetchAll}
              checked={checked}
              handleChangeInstance={handleChangeInstance}
              deactivateCpi={deactivateCpi}
              fetchCpiConfig={fetchCpiConfig}
              slaDescriptionTooltipOpen={slaDescriptionTooltipOpen}
              setSlaDescriptionTooltipOpen={setSlaDescriptionTooltipOpen}
              setChecked={setChecked}
            />
          </LibraryDetailsContainer>
        </LibraryDetailsProvider>
      )}
    </Box>
  )
}

export default CPIDetails
